import { registerLocaleData } from '@angular/common';
import localeEnAu from '@angular/common/locales/en-AU';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { environment } from './environments/environment';

registerLocaleData(localeEnAu);
defineCustomElements(window);

/**
 * Loads the Maps JavaScript API once before boot so `GoogleMap` finds `window.google`.
 * Uses `loading=async` plus `callback` (Google’s recommended pattern). Do not rely on
 * `script.onload` for readiness — it can fire before `importLibrary` is defined.
 * @see https://developers.google.com/maps/documentation/javascript/load-maps-js-api
 */
function loadGoogleMapsJsApi(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve();
  }
  const key = environment.mapsApiKey?.trim();
  if (!key) {
    return Promise.resolve();
  }
  if (
    window.google?.maps?.Map &&
    typeof window.google.maps.importLibrary === 'function'
  ) {
    return Promise.resolve();
  }
  const existing = document.querySelector(
    'script[data-training-guide-maps]',
  ) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 120;
      const id = window.setInterval(() => {
        attempts += 1;
        if (
          window.google?.maps?.Map &&
          typeof window.google.maps.importLibrary === 'function'
        ) {
          window.clearInterval(id);
          resolve();
        } else if (attempts >= maxAttempts) {
          window.clearInterval(id);
          reject(
            new Error(
              'Google Maps script is present but API did not become ready',
            ),
          );
        }
      }, 50);
    });
  }
  return new Promise((resolve, reject) => {
    const g = window as unknown as Record<string, () => void>;
    const callbackName = `__trainingGuideMapsInit_${Math.random().toString(36).slice(2)}`;
    g[callbackName] = () => {
      delete g[callbackName];
      resolve();
    };
    const s = document.createElement('script');
    s.setAttribute('data-training-guide-maps', '');
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&libraries=maps,marker,places&v=weekly&callback=${callbackName}`;
    s.onerror = () => {
      delete g[callbackName];
      reject(new Error('Google Maps script failed to load'));
    };
    document.head.appendChild(s);
  });
}

void loadGoogleMapsJsApi()
  .then(() => bootstrapApplication(AppComponent, appConfig))
  .catch((err) => console.error(err));
